import * as _ from 'lodash';

import { finleyAssert } from '@finley-technologies/common';

import {
  BankAccountFormFields,
  getConnectionProviderConfig,
} from './bankAccountFormConfigs';
import { GetInstitutionAndConnectionDetailsForBorrowerIdQueryGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { GetAllBankInstitutionsQueryGQLType } from '~/packages/bankAccounts/requests/bankAccounts.gql.generated';

type InstitutionAndConnectionDetails = GetInstitutionAndConnectionDetailsForBorrowerIdQueryGQLType['institutionAndConnectionDetailsForBorrower'];

export const getFinancialInstitutionOptions = ({
  bankInstitutions,
}: {
  bankInstitutions: GetAllBankInstitutionsQueryGQLType['allBankInstitutions'];
}) => {
  return _.uniqBy(
    _.map(bankInstitutions, (bankInstitution) => {
      finleyAssert(bankInstitution.id, 'bankInstitutionId must exist');
      return {
        value: bankInstitution.id,
        label: bankInstitution.name,
      };
    }),
    'value',
  );
};

export const getConnectionProviderSelectOptions = ({
  institutionAndConnectionDetails,
  selectedFinancialInstitutionId,
}: {
  institutionAndConnectionDetails: InstitutionAndConnectionDetails;
  selectedFinancialInstitutionId: string;
}) => {
  const connectionProviderConfig = getConnectionProviderConfig({
    institutionAndConnectionDetails,
  });

  const connectionProviderOptions = _.compact(
    _.map(connectionProviderConfig, (connectionProvider, key) => {
      if (
        connectionProvider.bankInstitutionId === selectedFinancialInstitutionId
      ) {
        return {
          value: key,
          label: connectionProvider.connectionSelectOptionName,
        };
      }
      return null;
    }),
  );

  connectionProviderOptions.push({ value: 'None', label: 'None' });

  return connectionProviderOptions;
};

export const getSelectedOptions = ({
  values,
  bankInstitutions,
  institutionAndConnectionDetails,
}: {
  values: BankAccountFormFields;
  bankInstitutions: GetAllBankInstitutionsQueryGQLType['allBankInstitutions'];
  institutionAndConnectionDetails: InstitutionAndConnectionDetails;
}) => {
  const { accountholder, financialInstitution, connectionProvider } = values;

  const selectedBankInstitution = _.find(bankInstitutions, {
    id: financialInstitution,
  });
  finleyAssert(
    selectedBankInstitution && selectedBankInstitution.id,
    'selectedBankInstitution id must exist from db list of institutions for a given selected financial institution',
  );
  const selectedBankInstitutionId = selectedBankInstitution.id;

  const connectionProviderConfig = getConnectionProviderConfig({
    institutionAndConnectionDetails,
  });

  const selectedConnectionProviderConfig =
    connectionProviderConfig[connectionProvider];

  let selectedTellerAccountId;
  let selectedPlaidAccountId;

  if (connectionProvider !== 'None') {
    const {
      connectionAccountId,
      connectionProvider: selectedConnectionProvider,
    } = selectedConnectionProviderConfig;
    if (selectedConnectionProvider === 'teller') {
      selectedTellerAccountId = connectionAccountId;
    }
    if (selectedConnectionProvider === 'plaid') {
      selectedPlaidAccountId = connectionAccountId;
    }
  }

  return {
    selectedAccountHolderId: accountholder,
    selectedBankInstitutionId,
    selectedTellerAccountId,
    selectedPlaidAccountId,
  };
};
